import axios from "axios";
import {
    getAccessToken, getRefreshToken,
    setTokens, clearTokens, loadTokens,
} from "@/lib/tokenStore";

// Resolve the API base URL **at runtime** from the browser's location.
//
// Why not just trust REACT_APP_BACKEND_URL from .env at build time?
// CRA inlines that variable into the JS bundle when `yarn build` runs. If the
// production deploy is built with the *preview* hostname baked in (as happened
// once already), every user on mywyngx.com gets blocked by CORS. By computing
// the API origin from `window.location.origin` we sidestep that whole class of
// build-time mishap: the frontend always talks to the same host the user
// loaded it from.
//
// REACT_APP_BACKEND_URL is still respected as an *override* — useful for the
// Capacitor iOS bundle (which loads file:// and must point at a real domain)
// and for local development where the browser tab is on :3000 but the API is
// on :8001 (handled by Kubernetes ingress in the preview env).
export const BACKEND_URL = (() => {
    if (typeof window !== "undefined") {
        const env = process.env.REACT_APP_BACKEND_URL;
        // Capacitor iOS WebView uses `capacitor://localhost` (iOS) or
        // `https://localhost` (Android). file:// is legacy. In all three
        // cases we MUST use the env override or every API call will hit
        // the local bundle host and 404.
        if (window.location.protocol === "capacitor:") return env || "";
        if (window.location.protocol === "file:") return env || "";
        // localhost dev → use env override so :3000 talks to :8001 via ingress.
        if (window.location.hostname === "localhost") return env || "";
        // Anywhere else (preview, production, custom domain) → same origin.
        return window.location.origin;
    }
    return process.env.REACT_APP_BACKEND_URL || "";
})();
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    // CRITICAL: withCredentials toggles per-environment to navigate two
    // conflicting realities:
    //
    //  · PREVIEW (Kubernetes ingress): the ingress overrides backend
    //    CORS and returns `Access-Control-Allow-Origin: *`. CORS spec
    //    forbids combining `*` with credentials, so credentials MUST
    //    be off here.
    //
    //  · PRODUCTION (Cloudflare → FastAPI direct): the backend echoes
    //    the actual Origin (`capacitor://localhost`) back and sets
    //    `Access-Control-Allow-Credentials: true`. Without cookies,
    //    Cloudflare's bot management re-issues its `__cf_bm` cookie
    //    on every single request → on a fresh iOS WebView this
    //    sometimes stalls the POST indefinitely, manifesting as a
    //    forever-loading "Inloggen…" spinner.  Enabling credentials
    //    lets the cookie persist across requests, which both unblocks
    //    the bot challenge AND removes the hang.
    //
    // We detect "is this prod?" by sniffing the resolved BACKEND_URL
    // hostname. mywyngx.com (apex or any subdomain) ⇒ prod.
    withCredentials: (() => {
        try {
            const host = new URL(BACKEND_URL || "http://x").hostname;
            return host === "mywyngx.com" || host.endsWith(".mywyngx.com");
        } catch { return false; }
    })(),
});

api.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Silent refresh on 401 — when the short-lived access token expires we
// transparently swap it for a fresh one using the long-lived refresh
// token from secure storage, then replay the original request. The user
// never sees a login screen as long as the refresh token is still valid
// (30 days). Concurrent requests during the refresh window queue up on
// the same Promise so we only hit /auth/refresh once.
let refreshInflight = null;
async function attemptRefresh() {
    if (refreshInflight) return refreshInflight;
    const refresh = getRefreshToken();
    if (!refresh) return null;
    refreshInflight = (async () => {
        try {
            const resp = await axios.post(`${API}/auth/refresh`,
                                          { refresh_token: refresh },
                                          { withCredentials: false });
            const newAccess  = resp?.data?.access_token  || null;
            const newRefresh = resp?.data?.refresh_token || refresh;
            if (newAccess) {
                await setTokens({ access: newAccess, refresh: newRefresh });
                return newAccess;
            }
            return null;
        } catch {
            await clearTokens();
            return null;
        } finally {
            refreshInflight = null;
        }
    })();
    return refreshInflight;
}

api.interceptors.response.use(
    (r) => r,
    async (e) => {
        const status = e?.response?.status;
        const url = e.config?.url || "";
        const original = e.config || {};

        // ── Normalize FastAPI 422 validation error shape ──
        // Pydantic returns `detail` as an ARRAY of {loc, msg, type, …}
        // objects. ~50 places across the codebase do
        // `toast.error(err?.response?.data?.detail || "fallback")`. When
        // a 422 fires that array goes straight into the toast renderer
        // and React crashes with "Objects are not valid as a React child",
        // taking the whole tree with it. Flattening to a string here keeps
        // every existing call site safe without a global refactor.
        try {
            const d = e?.response?.data?.detail;
            if (Array.isArray(d)) {
                const first = d[0];
                e.response.data.detail =
                    typeof first === "string"
                        ? first
                        : (first?.msg || "Ongeldige invoer");
            } else if (d && typeof d === "object" && d.msg) {
                e.response.data.detail = d.msg;
            }
        } catch { /* response shape was already exotic — leave it alone */ }

        // Try transparent refresh on any 401 except endpoints where it
        // would loop (login/register/refresh themselves). NOTE: /auth/me
        // is INCLUDED in the refresh attempt — the cold-launch flow is
        // exactly: hydrate token cache → call /auth/me → 401 because the
        // 24h access token expired → silently swap refresh→new access →
        // retry /auth/me → user stays signed in for the full 90 days of
        // the refresh token. Previously /auth/me was in the skip list
        // which forced a magic-link re-login every time the app was
        // opened after the access token expired.
        const skipRefresh = url.endsWith("/auth/login")
                            || url.endsWith("/auth/register")
                            || url.endsWith("/auth/refresh");
        if (status === 401 && !skipRefresh && !original._retry) {
            const fresh = await attemptRefresh();
            if (fresh) {
                original._retry = true;
                original.headers = { ...(original.headers || {}),
                                     Authorization: `Bearer ${fresh}` };
                return api.request(original);
            }
        }

        // /auth/me 401 = truly logged out → wipe both tokens.
        if (status === 401 && url.endsWith("/auth/me")) {
            await clearTokens();
        }

        // Global "credits empty" gate — backend raises HTTP 402 whenever
        // consume_credits() can't deduct (see backend/credits.py). Any
        // action in the app that hits this — Coach chat, weekly-report
        // follow-up, future paid features — pops the universal top-up
        // modal mounted at the app root via <CreditsGate />. Try to parse
        // the required credits from the error message so the modal can
        // tell the user how many they need.
        if (status === 402) {
            try {
                const detail = e.response?.data?.detail || "";
                const m = /(\d+)\s*credit/i.exec(detail);
                const required = m ? parseInt(m[1], 10) : 0;
                window.dispatchEvent(new CustomEvent("wyngx:credits-empty", {
                    detail: {
                        required,
                        url: e.config?.url,
                        detail,
                    },
                }));
            } catch { /* swallow */ }
        }
        return Promise.reject(e);
    },
);

// Eagerly hydrate the token cache so the first API request (usually
// /auth/me) already carries the bearer header.
loadTokens();

export default api;
